import { FC, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { CloseModalButton } from '../../../../../../components/close-modal-button/index';
import Loader from '../../../../../../components/loader/index';
import { ModalWrapper, ModalWrapperProps } from '../../../../../../components/modal-wrapper/index';
import { SignatureRestoreAccessConfirmModal } from '../../../../../../components/signature-restore-access-confirm-modal';
import {
  Company,
  useLocale,
  useReactForm,
  useRevokedAccessBeforeSend,
} from '../../../../../../hooks/index';
import { getDocumentCategoryDisplayLabel } from '../../../../constants/document-category.constant';
import { DocumentCategory } from '../../../../enums/document-category.const';
import { signingModeLabelKey } from '../../../../templates/enums/template.const';
import { SignatureFormType } from '../../../enums/signature-form-type.const';
import { useQuickSign } from '../../../hooks/use-quick-sign.hook';
import { SignatureFormStore } from '../../../store/signature-form.store';
import {
  getQuickSignDefaultValues,
  getQuickSignSchemaForStep,
  quickSignFormSchema,
  QuickSignFormSchema,
  QuickSignSignatureTab,
} from '../../../validation/quick-sign.validation';
import { QuickSignStepFive } from './step-five';
import { QuickSignStepFour } from './step-four';
import { QuickSignStepOne } from './step-one';
import { QuickSignStepThree } from './step-three';
import { QuickSignStepTwo } from './step-two';

const clearSignatureTabMappings = (tabs: QuickSignSignatureTab[]): QuickSignSignatureTab[] =>
  tabs.map(({ anchorString }) => ({ anchorString }));

export const QuickSignModal: FC = () => {
  const { messagesLocale } = useLocale();
  const { selectedCompanyId } = Company.useSelected();
  const isOpen = SignatureFormStore(({ isOpen }) => isOpen);
  const type = SignatureFormStore(({ type }) => type);
  const closeForm = SignatureFormStore(({ closeForm }) => closeForm);
  const currentStep = SignatureFormStore(({ currentStep }) => currentStep);
  const setStep = SignatureFormStore(({ setStep }) => setStep);
  const totalSteps = SignatureFormStore(({ totalSteps }) => totalSteps);
  const prevCategoryRef = useRef<DocumentCategory | undefined>(undefined);
  const prevSubCategoryRef = useRef<string | undefined>(undefined);
  const prevAssignedSignersKeyRef = useRef<string | undefined>(undefined);
  const { isPending: isSubmitting, submitQuickSign } = useQuickSign();
  const {
    checkAndProceed,
    clearState: clearRevokedAccessModalState,
    handleConfirmRestore,
    isChecking: isCheckingRevokedAccess,
    isFlowActive: isRestoreAccessFlowActive,
    revokedSigners: revokedAccessSigners,
  } = useRevokedAccessBeforeSend();

  const closeQuickSignForm = () => {
    clearRevokedAccessModalState();
    closeForm();
  };

  const onClose = () => {
    if (isRestoreAccessFlowActive) return;
    closeQuickSignForm();
  };

  const handleQuickSignModalOpenChange = (open: boolean) => {
    if (open || isRestoreAccessFlowActive) return;
    closeQuickSignForm();
  };

  const handleQuickSignInteractOutside: ModalWrapperProps['onInteractOutside'] = (event) => {
    if (isRestoreAccessFlowActive) {
      event.preventDefault();
    }
  };

  const clearRevokedAccessModalStateOnly = () => {
    clearRevokedAccessModalState();
  };

  const sendQuickSign = (data: QuickSignFormSchema) => {
    submitQuickSign({
      data,
      options: {
        onSuccess: () => closeQuickSignForm(),
      },
    });
  };

  const runRevokedAccessCheckAndSend = async (data: QuickSignFormSchema) => {
    const stakeholderIds = data.stepThree.signatorySlots
      .map((slot) => slot.stakeholder?.id)
      .filter((id): id is string => Boolean(id));

    await checkAndProceed(stakeholderIds, () => sendQuickSign(data));
  };

  const {
    control,
    clearErrors,
    getValues,
    handleSubmit,
    setError,
    setValue,
    trigger,
    watch,
    reset,
  } = useReactForm({
    defaultValues: getQuickSignDefaultValues(),
    schema: quickSignFormSchema,
  });

  const category = watch('stepOne.category');
  const signingMode = watch('stepTwo.signingMode');
  const signatorySlots = useWatch({ control, name: 'stepThree.signatorySlots' }) ?? [];
  const signatorySlotsKey = JSON.stringify(
    signatorySlots.map((slot) => [slot.slotId, slot.stakeholder?.id ?? null]),
  );

  useEffect(() => {
    if (!isOpen) return;
    reset(getQuickSignDefaultValues());
    prevCategoryRef.current = undefined;
    prevSubCategoryRef.current = undefined;
    prevAssignedSignersKeyRef.current = undefined;
  }, [isOpen, reset]);

  useEffect(() => {
    const previousKey = prevAssignedSignersKeyRef.current;
    prevAssignedSignersKeyRef.current = signatorySlotsKey;

    if (previousKey === undefined || previousKey === signatorySlotsKey) return;

    const tabs = getValues('stepFour.tabs') ?? [];
    if (tabs.length === 0) return;

    setValue('stepFour.tabs', clearSignatureTabMappings(tabs), { shouldValidate: true });
    setValue('stepFour.signatureSettingsCompleted', false, { shouldValidate: true });
  }, [getValues, setValue, signatorySlotsKey]);

  useEffect(() => {
    if (!isOpen) return;

    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || name == null) return;

      if (name === 'stepOne.category') {
        const next = value?.stepOne?.category;
        if (prevCategoryRef.current !== undefined && prevCategoryRef.current !== next) {
          setValue('stepOne.subCategory', undefined, { shouldValidate: false });
          setValue('stepOne.linkedTargetId', undefined, { shouldValidate: false });
          prevSubCategoryRef.current = undefined;
        }
        prevCategoryRef.current = next;
        return;
      }

      if (name === 'stepOne.subCategory') {
        const next = value?.stepOne?.subCategory;
        if (prevSubCategoryRef.current !== undefined && prevSubCategoryRef.current !== next) {
          setValue('stepOne.linkedTargetId', undefined, { shouldValidate: false });
        }
        prevSubCategoryRef.current = next;
      }
    });

    return () => subscription.unsubscribe();
  }, [isOpen, setValue, watch]);

  const categoryLabel = useMemo(
    () => getDocumentCategoryDisplayLabel(category, messagesLocale),
    [category, messagesLocale],
  );

  const signingModeLabel = useMemo(() => {
    if (!signingMode) return null;
    return messagesLocale[signingModeLabelKey[signingMode]];
  }, [messagesLocale, signingMode]);

  const categoryDocumentLabel = useMemo(() => {
    if (!categoryLabel) return null;
    return `${categoryLabel} document`;
  }, [categoryLabel]);

  const schemaForCurrentStep = useMemo(() => getQuickSignSchemaForStep(currentStep), [currentStep]);

  const watchValues = watch();
  const isCurrentStepValid = useMemo(() => {
    return schemaForCurrentStep.safeParse(watchValues).success;
  }, [schemaForCurrentStep, watchValues]);

  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    const step = SignatureFormStore.getState().currentStep;
    const total = SignatureFormStore.getState().totalSteps;
    if (step >= total) return;

    const stepKey =
      step === 1
        ? 'stepOne'
        : step === 2
          ? 'stepTwo'
          : step === 3
            ? 'stepThree'
            : step === 4
              ? 'stepFour'
              : undefined;
    if (stepKey) {
      const valid = await trigger(stepKey);
      if (!valid) return;
    }
    setStep(step + 1);
  };

  const handlePrev = () => setStep(Math.max(1, currentStep - 1));

  const onSubmit = (data: QuickSignFormSchema) => {
    if (currentStep !== totalSteps) return;
    void runRevokedAccessCheckAndSend(data);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      await handleNext();
      return;
    }
    await handleSubmit(onSubmit)(e);
  };

  const isLastStep = currentStep === totalSteps;
  const isSendDisabled =
    !isCurrentStepValid ||
    isSubmitting ||
    isCheckingRevokedAccess ||
    revokedAccessSigners.length > 0;

  const stepContent = useMemo(() => {
    if (currentStep === 1) return <QuickSignStepOne control={control} />;
    if (currentStep === 2) return <QuickSignStepTwo control={control} setValue={setValue} />;
    if (currentStep === 3 && selectedCompanyId) {
      return (
        <QuickSignStepThree
          clearErrors={clearErrors}
          companyId={selectedCompanyId}
          control={control}
        />
      );
    }
    if (currentStep === 4) {
      return (
        <QuickSignStepFour
          clearErrors={clearErrors}
          control={control}
          setError={setError}
          setValue={setValue}
        />
      );
    }
    if (currentStep === 5) {
      return <QuickSignStepFive companyId={selectedCompanyId ?? ''} control={control} />;
    }
    return (
      <div className="flex h-full w-full items-center justify-center bg-white px-6 py-8 text-sm text-gray-500">
        <AppFormattedMessage id="COMING_SOON" />
      </div>
    );
  }, [clearErrors, control, currentStep, selectedCompanyId, setError, setValue]);

  if (type !== SignatureFormType.QUICK_SIGN) {
    return null;
  }

  return (
    <>
      <ModalWrapper
        className="w-full max-w-[485px] p-1"
        control={{ onOpenChange: handleQuickSignModalOpenChange, open: isOpen }}
        disabledBgAutoClose={isRestoreAccessFlowActive ? true : undefined}
        onBgClick={onClose}
        onInteractOutside={handleQuickSignInteractOutside}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg">
          <div className="flex w-full items-start justify-between bg-white px-6 pt-3">
            <div className="flex min-w-0 flex-1 flex-col pr-3">
              <span className="text-xl font-semibold text-gray-700">
                <AppFormattedMessage id="QUICK_SIGN" />
              </span>
              <span className="block min-h-[12px] truncate pb-3 text-xs font-normal leading-[18px] text-gray-500">
                {categoryDocumentLabel &&
                  (signingModeLabel && currentStep >= 2 ? (
                    <AppFormattedMessage
                      id="QUICK_SIGN_SUBTITLE_WITH_SIGNING_MODE"
                      values={{
                        categoryDocument: categoryDocumentLabel,
                        signingMode: signingModeLabel,
                      }}
                    />
                  ) : (
                    <AppFormattedMessage
                      id="QUICK_SIGN_CATEGORY_DOCUMENT"
                      values={{ category: categoryLabel ?? '' }}
                    />
                  ))}
              </span>
            </div>
            <CloseModalButton onClose={onClose} />
          </div>

          <form
            className="flex h-full flex-col overflow-hidden bg-gray-25"
            onSubmit={handleFormSubmit}
          >
            <div className="flex w-full items-center gap-[5px] bg-white px-6 pt-2">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  className={twMerge(
                    'h-1 w-full rounded-[25px]',
                    i + 1 <= currentStep ? 'bg-[#12B76A]' : 'bg-gray-100',
                    i + 1 === currentStep &&
                      currentStep === totalSteps &&
                      'bg-gradient-to-r from-forest-500 to-forest-300',
                  )}
                  key={i}
                />
              ))}
            </div>

            <div className="flex h-full flex-col gap-4 overflow-hidden bg-gray-100">
              {stepContent}
            </div>

            <div className="flex items-center justify-between bg-gray-100 px-6 py-4">
              <button
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
                onClick={onClose}
                type="button"
              >
                <AppFormattedMessage id="CANCEL" />
              </button>
              <div className="flex gap-3">
                <Button
                  className="border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={currentStep === 1}
                  onClick={handlePrev}
                  styleType="DEFAULT_ROUNDED"
                  type="button"
                >
                  <AppFormattedMessage id="BACK" />
                </Button>
                {isLastStep ? (
                  <Button
                    className="bg-brand-700 px-6 py-2 text-sm font-medium text-white disabled:bg-gray-100 disabled:text-gray-300"
                    disabled={isSendDisabled}
                    styleType="DEFAULT_ROUNDED"
                    type="submit"
                  >
                    {isSubmitting || isCheckingRevokedAccess ? (
                      <Loader iconClassName="size-5" />
                    ) : (
                      <AppFormattedMessage id="SEND_FOR_SIGNATURES" />
                    )}
                  </Button>
                ) : (
                  <Button
                    className="bg-brand-700 px-6 py-2 text-sm font-medium text-white disabled:bg-gray-100 disabled:text-gray-300"
                    disabled={!isCurrentStepValid}
                    onClick={handleNext}
                    styleType="DEFAULT_ROUNDED"
                    type="button"
                  >
                    <AppFormattedMessage id="NEXT" />
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </ModalWrapper>

      <SignatureRestoreAccessConfirmModal
        isOpen={revokedAccessSigners.length > 0}
        onClose={clearRevokedAccessModalStateOnly}
        onConfirm={handleConfirmRestore}
        signers={revokedAccessSigners}
      />
    </>
  );
};
